import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6 },
  }),
};

export default function HairPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((r) => setServices(r.data))
      .catch(console.error);
  }, []);

  return (
    <div className="pt-28 lg:pt-44" data-testid="hair-page">
      <section className="relative py-12 md:py-16">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_high-on-life-1/artifacts/es82m8sl_664BE88C-A36D-43DB-B08B-F98CD462A848-308-000000087EB9114D.jpg"
            alt="Jax sunset hair styling"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-white">
          <p className="font-accent text-2xl text-sunset mb-2">bespoke services</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4">
            Hair & Massage
          </h1>
          <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-6">
            Specialising in curly & wavy hair, natural styling and Indian head massage. One chair,
            one client, one bespoke experience.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="https://tinyurl.com/2nxodcgu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-olive text-white hover:bg-amber rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1"
              data-testid="hair-book-btn"
            >
              Book Your Appointment
            </a>
            <a
              href="/curlscan"
              className="inline-block bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 border border-white/30"
              data-testid="hair-curlscan-btn"
            >
              Try The Curl Edit App
            </a>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <p className="font-accent text-xl text-sunset mb-1">what I offer</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">
              Services & Pricing
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bg-warmcream rounded-3xl p-8 border border-ocean/5 hover:shadow-lg transition-all duration-300"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-display text-lg">{service.name}</h3>
                    <span className="text-xs text-sage uppercase tracking-wider">
                      {service.category}
                    </span>
                  </div>
                  <span className="font-display text-2xl text-sage">
                    &pound;{service.price.toFixed(0)}
                  </span>
                </div>
                <p className="text-charcoal/60 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                {service.duration && (
                  <div className="flex items-center gap-1 text-xs text-charcoal/40">
                    <Clock size={12} />
                    <span>{service.duration}</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {services.length === 0 && (
            <div className="text-center py-12">
              <p className="text-charcoal/40">Loading services...</p>
            </div>
          )}

          <div className="mt-12 text-center">
            <a
              href="https://tinyurl.com/2nxodcgu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-ocean text-white hover:bg-charcoal rounded-full px-8 py-3.5 font-medium transition-all duration-300"
              data-testid="hair-book-bottom-btn"
            >
              View Full Price List & Book
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-peachlight">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <Sparkles className="text-sunset mb-6 mx-auto" size={32} />
            <blockquote className="font-display text-2xl sm:text-3xl tracking-tight italic mb-6">
              "Your curls are your superpower. Let me help you fall in love with them."
            </blockquote>
            <p className="font-accent text-lg text-sunset">— Jax</p>
          </div>
        </div>
      </section>
    </div>
  );
}
